import type {RRDate} from "../../..";
import type {Goal} from "./goal";
import {Package} from "./package";
import {Schedule} from "./schedule";
import type {Service} from "./service";
import type {DKK} from "./types";

/**
 * It represents a profile in our DB.
 * Profiles represent a given person in a given area, which they cover.
 *
 * Each profile can provide multiple services.
 * Each profile can set its own AV.
 * The settings of a given profile, are taken into account, when evaluating AV services in the bookingflow.
 */
interface Offer {
  service: Service;
  price: DKK;
}

export class Partner {
  id: number;
  alias: string;
  schedule: Schedule = new Schedule();
  offers: Map<Service["id"], Offer> = new Map();
  birdDistance?: number = undefined;

  constructor(id: number, alias: string) {
    this.id = id;
    this.alias = alias;
  }

  public getFirstAvOfPartner = (selectedService: Service): RRDate | "NO_AVAILABLE" => {
    return this.schedule.firstDayAvailable(selectedService.duration);
  };

  public canServiceBeProvidedOnDate = (date: RRDate, service: Service): boolean => {
    return this.schedule.canFit(date, service.duration);
  };

  /**
   * Partner will provide given service, and service will add partner
   * This is double directed association
   *
   * @param service - which given partner provides
   * @param price - in DKK for given service
   */
  public addServiceType = (service: Service, price: DKK): void => {
    const offerExists = this.offers.get(service.id);
    if (!offerExists) {
      const offer = {service, price};
      this.offers.set(service.id, offer);
      service.addPartner(this, price);
    }
  };

  public get hasTimeslots(): boolean {
    return this.schedule.hasTimeslots();
  }

  public getPrice(service: Service): DKK {
    const offer = this.offers.get(service.id);
    if (!offer) throw new Error("Partner does not provide this service");
    return offer.price;
  }

  public get servicetypes(): Service[] {
    return [...this.offers.values()].map((offer) => offer.service);
  }

  private getPackages = (goal: Goal, service?: Service): Package[] => {
    const consideredServices: Service[] = [];
    if (!service) {
      consideredServices.push(...this.servicetypes.filter((service) => service.goal.id === goal.id && service.packages.length > 0));
    } else {
      consideredServices.push(service);
    }
    const packages: Package[] = consideredServices.reduce((p, service) => {
      const packagesOfService: Package[] = service.packages.map((packageTemplate) => {
        return Package.fromPackageTemplate(packageTemplate, this);
      });
      p.push(...packagesOfService);
      return p;
    }, [] as Package[]);
    return packages;
  };

  private getCheapestPackage = (packages: Package[]): Package | undefined => {
    const cheapest = packages.reduce((p, aPackage) => {
      const cheaper = p.getPrice() !== undefined && p.getPrice() < aPackage.getPrice() ? p : aPackage;
      return cheaper;
    });
    if (cheapest === ({} as Package)) return undefined;
    return cheapest;
  };

  private getLowestDiscountPackage = (packages: Package[]): Package | undefined => {
    const highestDiscounted = packages.reduce((p, aPackage) => {
      const higherDiscounted = p.getDiscountAmount() !== undefined && p.getDiscountAmount() < aPackage.getDiscountAmount() ? p : aPackage;
      return higherDiscounted;
    });
    if (highestDiscounted === ({} as Package)) return undefined;
    return highestDiscounted;
  };

  public getSaveFromAndPriceFrom(goal: Goal, service?: Service): {priceFrom: DKK; saveMin: DKK} {
    const packages = this.getPackages(goal, service);
    if (packages.length == 0) return {saveMin: 0, priceFrom: 0};
    const priceFrom = this.getCheapestPackage(packages)?.getPrice() || 0;
    const saveMin = this.getLowestDiscountPackage(packages)?.getDiscountAmount() || 0;

    return {saveMin, priceFrom};
  }

  /**
   * @param partner - to compare current partner against
   * @returns true if ids are matching
   */
  public equals(partner: Partner): boolean {
    return this.id === partner.id;
  }
}
